import { useState, useEffect, useRef } from 'react';
import { useSeoMeta } from '@unhead/react';
import { useBitcoinBlocks, type BitcoinBlock } from '@/hooks/useBitcoinBlocks';
import { useSpookySounds } from '@/hooks/useSpookySounds';
import { useToast } from '@/hooks/useToast';
import { useTheme } from '@/hooks/useTheme';
import { CemeteryHeader } from '@/components/CemeteryHeader';
import { CemeteryFooter } from '@/components/CemeteryFooter';
import { StatsBar } from '@/components/StatsBar';
import { Tombstone } from '@/components/Tombstone';
import { BlockDetailsModal } from '@/components/BlockDetailsModal';
import { MempoolSpirits } from '@/components/MempoolSpirits';
import { CemeteryChat } from '@/components/CemeteryChat';
import { Skeleton } from '@/components/ui/skeleton';
import { AlertCircle, Skull } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';

const Index = () => {
  useSeoMeta({
    title: 'Block Cemetery - The Haunted Bitcoin Explorer',
    description: 'Where Bitcoin Blocks Go to Rest... Until the Next One Rises. A live blockchain explorer with a spooky twist.',
  });

  const { data: blocks, isLoading, error } = useBitcoinBlocks();
  const { playThunder } = useSpookySounds();
  const { toast } = useToast();
  const [selectedBlock, setSelectedBlock] = useState<BitcoinBlock | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const previousBlockHeightRef = useRef<number | null>(null);

  // Play thunder sound and show notification when new block arrives
  useEffect(() => {
    if (blocks && blocks.length > 0) {
      const latestBlock = blocks[0];

      if (previousBlockHeightRef.current !== null && latestBlock.height > previousBlockHeightRef.current) {
        playThunder();

        toast({
          title: (
            <div className="flex items-center gap-2">
              <Skull className="h-5 w-5" />
              <span>A New Block Rises from the Grave!</span>
            </div>
          ),
          description: `Block #${latestBlock.height.toLocaleString()} with ${latestBlock.tx_count} transactions has been mined.`,
          duration: 5000,
        });
      }

      previousBlockHeightRef.current = latestBlock.height;
    }
  }, [blocks, playThunder, toast]);

  const handleBlockClick = (block: BitcoinBlock) => {
    setSelectedBlock(block);
    setModalOpen(true);
  };

  const { theme } = useTheme();
  const isNightmare = theme === 'nightmare';

  return (
    <div className={`min-h-screen flex flex-col bg-background ${isNightmare ? 'nightmare-mode' : ''}`}>
      <CemeteryHeader />
      <StatsBar />

      <main className="flex-1 relative">
        <MempoolSpirits />

        {/* Nightmare mode effects */}
        {isNightmare && (
          <>
            <div className="absolute inset-0 pointer-events-none">
              <div className="absolute top-20 left-10 text-6xl opacity-20 skull-float">💀</div>
              <div className="absolute top-40 right-20 text-5xl opacity-20 skull-float" style={{ animationDelay: '1s' }}>💀</div>
              <div className="absolute top-60 left-1/3 text-7xl opacity-20 skull-float" style={{ animationDelay: '2s' }}>💀</div>
              <div className="absolute top-80 right-1/4 text-6xl opacity-20 skull-float" style={{ animationDelay: '3s' }}>💀</div>
              <div className="absolute top-32 left-1/2 text-4xl opacity-30 vampire-bat">🦇</div>
              <div className="absolute top-72 right-10 text-4xl opacity-30 vampire-bat" style={{ animationDelay: '2s' }}>🦇</div>
              <div className="absolute top-52 left-20 text-5xl opacity-25 vampire-bat" style={{ animationDelay: '4s' }}>🧛</div>
              <div className="absolute top-96 right-32 text-5xl opacity-25 vampire-bat" style={{ animationDelay: '5s' }}>🧛</div>
            </div>
          </>
        )}

        <div className="container mx-auto px-4 py-8 relative z-10">
          {/* Welcome Section */}
          <div className="text-center mb-12 space-y-4">
            <h2 className="text-3xl md:text-4xl font-gothic text-primary">
              Welcome to the Eternal Ledger
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Each block is a tombstone in the cemetery of proof-of-work.
              Watch as new graves emerge from the digital fog, carrying the weight of countless transactions
              into the eternal blockchain.
            </p>
          </div>

          {/* Error State */}
          {error && (
            <Alert variant="destructive" className="mb-8">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Error</AlertTitle>
              <AlertDescription>
                Failed to fetch blocks from the Bitcoin blockchain. The spirits are restless...
              </AlertDescription>
            </Alert>
          )}

          {/* Loading State */}
          {isLoading && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {Array.from({ length: 12 }).map((_, i) => (
                <Skeleton key={i} className="h-48 w-full" />
              ))}
            </div>
          )}

          {/* Blocks Grid */}
          {blocks && blocks.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {blocks.map((block, index) => (
                <Tombstone
                  key={block.id}
                  block={block}
                  onClick={() => handleBlockClick(block)}
                  index={index}
                />
              ))}
            </div>
          )}

          {/* Empty State */}
          {blocks && blocks.length === 0 && !isLoading && (
            <div className="text-center py-16">
              <p className="text-muted-foreground text-lg">
                The cemetery is empty... waiting for the dead to arrive.
              </p>
            </div>
          )}
        </div>
      </main>

      <CemeteryFooter />

      {/* Block Details Modal */}
      <BlockDetailsModal
        block={selectedBlock}
        open={modalOpen}
        onOpenChange={setModalOpen}
      />

      {/* Cemetery Chat */}
      <CemeteryChat />
    </div>
  );
};

export default Index;
